import React, { ReactNode } from 'react';

import uniqueId from 'lodash/uniqueId';

import { mdiClose } from '@lumapps/lumx/icons';
import { Dialog, Toolbar, List, DialogProps, Size, IconButton, Emphasis } from '@lumapps/lumx/react';
import { useTranslate, GLOBAL } from '@lumapps/translations';

import { NAVIGATION_ADMIN } from '../../keys';

export interface NavigationItemWizardProps {
    /** whether the wizard is open or not */
    isOpen: boolean;
    /** reference to the button or parent element that opened the dialog */
    buttonRef: DialogProps['parentElement'];
    /** callback to be executed when the dialog closes */
    onClose: DialogProps['onClose'];
    /** whether it should prevent */
    preventAutoClose: DialogProps['preventAutoClose'];
    /** options to be displayed for the wizard */
    options?: ReactNode;
    /** components that will be rendered before the wizard options */
    beforeOptions?: ReactNode;
}

/**
 * Dialog that will display a set of options in order to create a navigation item. For the `options` prop,
 * the recommendation is to create a component that has the different types of navigation items that your use case allows,
 * like the component `NavigationItemWizardOptions` does.
 *
 * In that component, you should group the different options to display,
 * using either the already existing options (CommunityWizardOption, ContentWizardOption, etc), or creating new ones using the
 * `GlobalPickerOption` component.
 *
 * For example, if you are using the wizard to display 2 options (ExternalLinkWizardOption and SectionWizardOption) you should create
 * the following component:
 * export const YourWizardOptions: React.FC<NavigationItemWizardOptionProps> = (props) => {
        return (
            <>
                <SectionWizardOption {...props} />
                <ExternalLinkWizardOption {...props} />
            </>
        );
    };

 * Then, use that component and pass it in to `NavigationItemWizard`, while managing the other props:
    <NavigationItemWizard
        isOpen={isWizardOpen}
        buttonRef={buttonRef}
        onClose={() => toggleWizard(false)}
        preventAutoClose={wasOptionSelected}
        beforeOptions={beforeOptions}
        options={
            <YourWizardOptions
                defaultLanguage={defaultLanguage}
                languages={languages}
                onAdded={onNavigationItemAdded}
                toggleWizard={toggleWizard}
                setWasOptionSelected={setWasOptionSelected}
                options={options}
                scope={scope}
            />
        }
    />
 * @param NavigationItemWizardProps
 */
export const NavigationItemWizard: React.FC<NavigationItemWizardProps> = ({
    isOpen,
    buttonRef,
    onClose,
    options,
    preventAutoClose = false,
    beforeOptions,
}) => {
    const { translateKey } = useTranslate();
    const labelId = React.useMemo(() => uniqueId('navigation-item-wizard-title'), []);
    const listRef = React.useRef<HTMLUListElement>(null);

    return (
        <Dialog
            dialogProps={{ 'aria-labelledby': labelId }}
            isOpen={isOpen}
            parentElement={buttonRef}
            onClose={onClose}
            size={Size.regular}
            preventAutoClose={preventAutoClose}
            focusElement={listRef}
        >
            <header>
                <Toolbar
                    label={
                        <h2 id={labelId} className="lumx-typography-title">
                            {translateKey(NAVIGATION_ADMIN.ADD_NAVIGATION_ITEM)}
                        </h2>
                    }
                    after={
                        <IconButton
                            className="lumx-spacing-margin-right-regular"
                            emphasis={Emphasis.low}
                            icon={mdiClose}
                            onClick={onClose}
                            label={translateKey(GLOBAL.CLOSE)}
                        />
                    }
                />
            </header>
            {beforeOptions}

            <List className="lumx-spacing-margin-bottom-big" ref={listRef} tabIndex={-1}>
                {options}
            </List>
        </Dialog>
    );
};
